import React, { useMemo } from 'react';
import { EntitySecurityProvider, SECURITY_ACTIONS } from '../../common/auth/Security';
import type { SecuredActionType } from '../../common/components/ActionType';
import EditablePanel from '../../common/components/EditablePanel';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { useShallowMergeSxProps } from '../../common/hooks/useShallowMergeSxProps';
import I18n from '../../common/i18n/I18n';
import { Icons } from '../../common/icons/Icons';
import useHistory from '../../common/utils/useHistory';
import { ProductionCardViewProps } from './productionHeaderUtils';
import ProductionMetadataGrid from './ProductionMetadataGrid';
import ProductionOverviewDataGrid from './ProductionOverviewDataGrid';

export default function ProductionHeaderDataGrid({
    viewState,
    onEdit,
    setViewState,
    isSaving = false,
    isReadOnly = false,
    enableSelectItem = false,
    statusNote = undefined,
    sidePanelView = false,
    sx,
    defaultCollapsed = false
}: ProductionCardViewProps) {
    const customPanelActions = useMemo<SecuredActionType[]>(
        () =>
            isReadOnly
                ? []
                : [
                      {
                          securityAction: SECURITY_ACTIONS.EDIT,
                          icon: Icons.Edit,
                          tooltip: <I18n token="form.title.edit" />,
                          onClick: () => onEdit(),
                          disabled: isSaving
                      }
                  ],
        [isReadOnly, onEdit, isSaving]
    );
    const { location } = useHistory();
    const pathname = location?.pathname || '';
    const isInPeople = pathname.includes('/people/');
    const panelSx = useShallowMergeSxProps(
        {
            position: 'relative',
            borderRadius: 1,
            bgcolor: enableSelectItem ? 'transparent' : 'background.paper',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            minHeight: 'fit-content'
        },
        sx
    );

    return (
        <EntitySecurityProvider entityName={ENTITY_CONSTANTS.production} entitySecurity={viewState.security}>
            <EditablePanel
                Header={
                    <ProductionOverviewDataGrid
                        viewState={viewState}
                        showOpenInNewTabLink={enableSelectItem}
                        sidePanelView={sidePanelView}
                    />
                }
                isCollapsable={!isReadOnly}
                securedActions={customPanelActions}
                sx={panelSx}
                defaultCollapsed={defaultCollapsed}
                useResponsiveActions={true}
                headerSx={isInPeople && { display: 'block' }}
            >
                <ProductionMetadataGrid
                    workspace={viewState}
                    setWorkspace={setViewState}
                    statusNote={statusNote}
                    isReadOnly={isReadOnly}
                    isSaving={isSaving}
                    sidePanelView={sidePanelView}
                />
            </EditablePanel>
        </EntitySecurityProvider>
    );
}
